import { Component, Input, OnInit, AfterViewInit, OnDestroy, SimpleChanges, OnChanges, ElementRef, ViewChild } from '@angular/core';
import { GetMediumListKomaFlagService } from './api-services/get-medium-list-koma-flag.service';
import { KomaMediumTypeComboBoxModel } from './koma-medium-type-combo-box.model';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PlessHttp } from '../../../../shared/http/pless-http.service';
import { PlessViewUtilsSub } from '../../../../commons/kymw00/pless-view-utils-sub.service';
import { PlessDialogServiceSub } from '../../../../commons/kymw00/pless-dialog-service-sub';

/**
 * 共通部品 全媒体コンボボックスを制御するクラス
 *
 * @export KomaMediumTypeComboBoxComponent
 * @class KomaMediumTypeComboBoxComponent
 * @implements {OnInit}
 * @implements {AfterViewInit}
 * @implements {OnChanges}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'app-koma-medium-type-combo-box',
  templateUrl: 'koma-medium-type-combo-box.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: KomaMediumTypeComboBoxComponent,
      multi: true
    },
    {
      provide: GetMediumListKomaFlagService,
      useFactory: (plessHttp: PlessHttp, dialogService: PlessDialogServiceSub) => {
        return new GetMediumListKomaFlagService(plessHttp, dialogService);
      },
      deps: [PlessHttp, PlessDialogServiceSub]

    },
  ]
})

export class KomaMediumTypeComboBoxComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {

  public selectedIndex: number = <number>-1;
  public selectedMediumType: string = <string>'';
  public selectedMediumName: string = <string>'';
  public selectedStopOrderCode: string = <string>'';
  public selectedProgressWorkNum: string = <string>'';
  public selectedProgressWorkNumName: string = <string>'';
  public selectedReceiptEOrderCode: string = <string>'';
  public selectedReceiptECatalogId: string = <string>'';
  public selectedMediumTypeClass: string = <string>'';
  public selectedDutiesFormDiv: string = <string>'';
  public model: KomaMediumTypeComboBoxModel = new KomaMediumTypeComboBoxModel();

  @ViewChild('comboBox', { static: true }) public comboBox: ElementRef;

  @Input() public subid: string = null;
  @Input() blank: boolean = <boolean>true;                                      // 空白フラグ
  @Input() allMediumType: boolean = <boolean>false;                             // 全生協表示フラグ
  @Input() initAutoLoad: boolean = <boolean>true;                               // オートロードフラグ
  @Input() orderCode: string = <string>'';                                      // 検索条件「受注コード」を設定する(voの値)
  @Input() orderCodeMediumeTypeClear: string;                                   // 検索条件「受注コード」を設定する(媒体タイプをクリアする)
  @Input() public tabindex: number = null;
  @Input() forcedEnabledMode: string = null;

  // レイアウト系の変数
  @Input() width: number | string;
  @Input() height: number = <number>20;
  @Input() required: boolean = <boolean>false;
  @Input() initFocusItem: any = null;                                           // ロード完了後フォーカスを移動させる
  @Input() afterFocus: any = null;                                              // ロード完了後フォーカスを移動させる
  @Input() visible: boolean = <boolean>true;                                    // 表示・非表示フラグ

  /** ngModel連携用 */
  @Input() public selectedValue: string = null;

  /**
   * Creates an instance of mediumTypeComboBox.
   * @memberof mediumTypeComboBox
   * @param {getMediumListKomaFlagService} GetMediumListKomaFlagService 全媒体リストを取得する
   */
  constructor(
    public elementRef: ElementRef,
    private getMediumListKomaFlagService: GetMediumListKomaFlagService,
  ) { }

  // ValueAccessor
  @Input() public disabled: boolean = <boolean>false;
  public onChange(_: any) { }
  public onTouched() { }
  public writeValue(text: string): void {
    if (text !== this.selectedValue) {
      this.selectedValue = text;
      this.setMediumType(text);
    }
  }
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * 初期化処理
   *
   * @memberof KomaMediumTypeComboBoxComponent
   */
  ngOnInit(): void {
    // コンボボックスインスタンス設定
    this.model.component = this;
    this.comboBox.nativeElement.setAttribute('id', this.subid + '_sub');
    this.comboBox.nativeElement.setAttribute('forcedEnabledMode', this.forcedEnabledMode);
  }

  /**
   * ロード完了時
   *
   * @memberof KomaMediumTypeComboBoxComponent
   */
  ngAfterViewInit(): void {
    if (this.model.initAutoLoad) {
      // データ取得
      this.getData();
    }
  }

  /**
   * 値変更時イベント
   *
   * @param {SimpleChanges} changes 変更イベント
   * @memberof KomaMediumTypeComboBoxComponent
   */
  ngOnChanges(changes: SimpleChanges): void {
    Object.keys(changes).forEach((propName) => {
      switch (propName) {
        case 'blank':
          // 先頭空白表示フラグ
          this.model.blank = changes.blank.currentValue;
          break;
        case 'allMediumType':
          // 全媒体表示表示フラグ
          this.model.allMediumType = changes.allMediumType.currentValue;
          break;
        case 'initAutoLoad':
          // 初期データ自動取得フラグ
          this.model.initAutoLoad = changes.initAutoLoad.currentValue;
          break;
        case 'orderCode':
          // 検索条件「受注コード」を設定するフラグ
          this.orderCode = changes.orderCode.currentValue;
          this.model.orderCode = changes.orderCode.currentValue;
          if (!PlessViewUtilsSub.isEmpty(this.orderCode)
             &&  this.orderCode.length === 7) {
            // オーダーコードがあればリロード
            this.getData();
          }
          break;
        case 'orderCodeMediumeTypeClear':
          // 検索条件「受注コード」を設定する(媒体タイプ)をクリアするフラグ
          this.orderCode = changes.orderCodeMediumeTypeClear.currentValue;
          this.model.orderCode = changes.orderCodeMediumeTypeClear.currentValue;
          this.model.setMediumType = ''; // 初期選択の初期化
          this.getData();
          break;
      }
    });
  }

  /**
   * コンポーネント終了時
   *
   * @memberof KomaMediumTypeComboBoxComponent
   */
  ngOnDestroy(): void {
    // コンボボックス情報初期化
    this.clear();
  }

  /**
   * クリア
   *
   * @memberof KomaMediumTypeComboBoxComponent
   */
  public clear(): void {
    // コンボボックス情報初期化
    this.selectedIndex = <number>-1;
    this.selectedMediumType = <string>'';
    this.selectedMediumName = <string>'';
    this.selectedStopOrderCode = <string>'';
    this.selectedProgressWorkNum = <string>'';
    this.selectedProgressWorkNumName = <string>'';
    this.selectedReceiptEOrderCode = <string>'';
    this.selectedReceiptECatalogId = <string>'';
    this.selectedMediumTypeClass = <string>'';
    this.selectedDutiesFormDiv = <string>'';
    this.model = new KomaMediumTypeComboBoxModel();
    this.model.component = this;
    this.model.blank = this.blank;
    this.model.allMediumType = this.allMediumType;
  }

  /**
   * コンボボックスの選択媒体Nameを切り替える
   *
   * @param {string} mediumType 媒体区分
   * @memberof KomaMediumTypeComboBoxComponent
   */
  public setMediumType(mediumType: string): void {
    this.model.setMediumType = mediumType;

    // ロード完了の場合
    if (this.model.completeUpdateFlg) {
      this.updateSelectedMediumType();
    }
  }

  /**
   * コンボボックスの選択コードNameを切り替える
   *
   * @memberof KomaMediumTypeComboBoxComponent
   */
  public updateSelectedMediumType(): void {
    const mediumList: Array<any> = this.model.mediumList;

    for (let i = Number(0); i < mediumList.length; i++) {
      if (mediumList[i].MediumType === this.model.setMediumType) {
        this.selectedIndex = i;
      }
    }

    if (this.afterFocus != null) {
      if (this.afterFocus instanceof HTMLElement) {
        this.afterFocus.focus();
      } else {
        // 参照先の型がElementでない(Component)ならComponent内の要素を見て最初のinput,textarea,selectElementにフォーカスをあてる
        for (const child of this.afterFocus.elementRef.nativeElement.children) {
          if (child instanceof HTMLInputElement || child instanceof HTMLTextAreaElement || child instanceof HTMLSelectElement) {
            child.focus();
            break;
          }
        }
      }
    }

    if (this.initFocusItem != null) {
      if (this.initFocusItem instanceof HTMLElement) {
        // 参照先の型がElementならそのままフォーカスをあてる
        this.initFocusItem.focus();
      } else {
        // 参照先の型がElementでない(Component)ならComponent内の要素を見て最初のinput,textarea,selectElementにフォーカスをあてる
        for (const child of this.initFocusItem.elementRef.nativeElement.children) {
          if (child instanceof HTMLInputElement || child instanceof HTMLTextAreaElement || child instanceof HTMLSelectElement) {
            child.focus();
            break;
          }
        }
      }

      this.initFocusItem = null;
    }

    // バインドフィールドを更新
    this.updateBindedField(null);
  }

  /**
   * フェーズ番号名称取得
   *
   * @private
   * @param {string} progressWorkNum
   * @param {string} progressWorkName
   * @returns {string}
   * @memberof KomaMediumTypeComboBoxComponent
   */
  private getProgressWorkNumName(progressWorkNum: string, progressWorkName: string): string {
    if (PlessViewUtilsSub.isEmpty(<string>progressWorkNum) || PlessViewUtilsSub.isEmpty(<string>progressWorkName)) {
      return PlessViewUtilsSub.nullToBlank(progressWorkNum) + PlessViewUtilsSub.nullToBlank(progressWorkName);
    } else {
      return PlessViewUtilsSub.nullToBlank(progressWorkNum) + ':' + PlessViewUtilsSub.nullToBlank(progressWorkName);
    }
  }

  /**
   * コンボボックスの内容を再度ロードする
   *
   * @param {string} [orderCode=''] 受注コード
   * @memberof KomaMediumTypeComboBoxComponent
   */
  public reload(orderCode = ''): void {
    this.orderCode = orderCode;
    this.getData();
  }

  /**
   * バインドフィールド初期化
   *
   * @param {*} e
   * @returns {void}
   * @memberof KomaMediumTypeComboBoxComponent
   */
  public updateBindedField(e: any): void {
    if (e != null) {
      this.selectedIndex = e.target.selectedIndex;
    }
    if (this.selectedIndex === -1) {
      this.selectedMediumType = <string>'';
      this.selectedMediumName = <string>'';
      this.selectedStopOrderCode = <string>'';
      this.selectedProgressWorkNum = <string>'';
      this.selectedProgressWorkNumName = <string>'';
      return;
    }

    this.selectedValue = this.model.mediumList[this.selectedIndex].MediumType;
    this.selectedMediumType = this.model.mediumList[this.selectedIndex].MediumType;
    this.selectedMediumName = this.model.mediumList[this.selectedIndex].MediumName;
    this.selectedStopOrderCode = this.model.mediumList[this.selectedIndex].StopOrderCode;
    this.selectedProgressWorkNum = this.model.mediumList[this.selectedIndex].ProgressWorkNum;
    this.selectedProgressWorkNumName = this.getProgressWorkNumName(
      this.model.mediumList[this.selectedIndex].ProgressWorkNum,
      this.model.mediumList[this.selectedIndex].ProgressWorkName
    );
    this.selectedReceiptEOrderCode = this.model.mediumList[this.selectedIndex].ReceiptEOrderCode;
    this.selectedReceiptECatalogId = this.model.mediumList[this.selectedIndex].ReceiptECatalogId;
    this.selectedMediumTypeClass = this.model.mediumList[this.selectedIndex].MediumTypeClass;
    this.selectedDutiesFormDiv = this.model.mediumList[this.selectedIndex].DutiesFormDiv;

    // 同じ意味合いのafterFocusがあればinitFocusItemに入れる
    if (this.afterFocus != null) {
      this.initFocusItem = this.afterFocus;
    }

    if (this.initFocusItem != null) {
      let queryedElement;
      if (this.initFocusItem instanceof HTMLElement) {
        queryedElement = this.initFocusItem.querySelector('textarea, input, select');
      } else {
        queryedElement = this.initFocusItem.elementRef.nativeElement.querySelector('textarea, input, select');
      }
      (queryedElement ? <HTMLElement>queryedElement : this.initFocusItem).focus();
    }
  }

  /**
   * データ取得
   *
   * @memberof KomaMediumTypeComboBoxComponent
   */
  public getData(): void {
    this.getMediumListKomaFlagService.execute(this.model, this.orderCode);
  }

}
